import {
  FieldFunctionOptions,
  InMemoryCache,
  isReference,
  type Reference,
  type StoreObject,
} from '@apollo/client';
import { relayStylePagination } from '@apollo/client/utilities';
import { DateTime } from 'luxon';
import { merge, cloneDeepWith, isString, keyBy } from 'lodash-es';
import { offsetLimitPagination, tableOffsetLimitPagination } from './policies/tableOffsetLimitPagination';
import { StrictTypedTypePolicies } from './createCache.generated';
import { trimSoftHyphen } from '../../utils/trimSoftHyphen';
import { profileImageToCdn } from '../../utils/profileImageToCdn';
import { isProject } from '../../constants/projectTarget';
import { mergeArrayByIndex } from './policies/mergeArrayByIndex';
import { mergeArrayByField } from './policies/mergeArrayByField';
import { mergeArrayByField as mergeChatMessagesByField } from './policies/mergeChatMessages';
import { mergeField } from './policies/mergeField';
import possibleTypes from './possibleTypes.json';

export const LOGIN_AUTH_TOKEN_KEY = 'mdLoginToken';

const toGroupPostTime = (value: unknown) => (isString(value) ? Date.parse(value) : NaN);

type GroupPostEntry = Reference | StoreObject;

function mergeGroupPostsById(
  existing: readonly GroupPostEntry[] | null,
  incoming: readonly GroupPostEntry[] | null,
  { args, readField }: FieldFunctionOptions,
) {
  if (incoming == null) return incoming;

  const idOf = (post: GroupPostEntry) => readField<string>('id', post);
  const identified = (list: readonly GroupPostEntry[] | null) =>
    (list ?? []).filter((post) => Boolean(idOf(post)));

  const input = args?.input as { startDate?: unknown; endDate?: unknown } | undefined;
  const windowStart = toGroupPostTime(input?.startDate);
  const windowEnd = toGroupPostTime(input?.endDate);
  const incomingIds = new Set(identified(incoming).map(idOf));

  // 조회 기간에 하루라도 걸치는 글은 이번 응답이 모두 담고 있다 — 빠졌다면 지워진 글이다
  const overlapsWindow = (post: GroupPostEntry) =>
    toGroupPostTime(readField('startDate', post)) <= windowEnd &&
    toGroupPostTime(readField('endDate', post)) >= windowStart;

  const kept = identified(existing).filter((post) => incomingIds.has(idOf(post)) || !overlapsWindow(post));
  const merged = merge(keyBy(kept, idOf), keyBy(identified(incoming), idOf));

  return Object.values(merged);
}

export function getConfig() {
  return {
    possibleTypes: possibleTypes,
    // @ts-ignore
    typePolicies: {
      GlobalModumAssignment: {
        keyFields: ['groupId'],
      },
      CurrentStudyState: {
        keyFields: ['groupId', 'studentId', 'managerId'],
        fields: {
          feedsByDatetime: {
            keyArgs: (args) => {
              if (!args?.dueDate) {
                return '';
              }

              const dateTime = DateTime.fromISO(args.dueDate);
              if (!dateTime.isValid) {
                return '';
              }

              return dateTime.startOf('hour').toISO();
            },
          },
        },
      },
      ProseMirrorContent: {
        fields: {
          html: { read: trimSoftHyphen },
          json: {
            read(json) {
              return cloneDeepWith(json, (value) => {
                if (isString(value)) {
                  return trimSoftHyphen(value);
                }
                return undefined;
              });
            },
          },
        },
      },
      ManagerGroupFeedNoticeAndEvent: {
        keyFields: ['feedId'],
      },
      ManagerGroupFeedStudyModule: {
        keyFields: ['feedId'],
      },
      CalendarDayItemType: {
        merge: true,
      },
      FindChatBotMessagesPayload: {
        fields: {
          messages: {
            merge(existing, incoming) {
              if (!existing) {
                return incoming;
              }

              return [...incoming, ...existing];
            },
          },
        },
      },
      AiTutorChannel: {
        fields: {
          messages: {
            keyArgs: false,
            merge(existing, incoming) {
              if (!existing) {
                return incoming;
              }

              return [...existing, ...incoming];
            },
          },
        },
      },
      Message: {
        fields: {
          sender: {
            merge: mergeField(),
          },
          isTemp: {
            read(isTemp = false) {
              return isTemp;
            },
          },
          uploadStatus: {
            read(uploadStatus = 'IDLE') {
              return uploadStatus;
            },
          },
        },
      },
      EmoticonPanel: {
        keyFields: [],
      },
      Channel: {
        fields: {
          currentStudyState: {
            merge: true,
          },
          lastReadMessage: {
            merge: mergeField(),
          },

          messages: {
            keyArgs: false,
            merge(existing, incoming, { args, readField, mergeObjects }) {
              if (!existing) {
                existing = {
                  nodes: [],
                };
              }
              if (!incoming) {
                return existing;
              }

              // message 전송 API에서 캐시를 업데이트한 것이라면 incoming Message를 뒤에 붙이고
              const existingNodes = existing?.nodes || [];
              // const incomingNodes = incoming?.nodes || [];
              const incomingNodes = incoming.nodes
                ? incoming.nodes.map((node: any) => {
                    if (isReference((node = { ...node }))) {
                      // readField not work here i don't know why
                      // @ts-ignore
                      node.id = node.__ref;
                    }
                    return node;
                  })
                : [];

              const mergeById = mergeChatMessagesByField('id');

              // cursor가 없는 경우(최초 로딩) 전체 덮어쓰기
              if (!args?.input?.cursor && existingNodes.length === 0) {
                return {
                  ...incoming,
                  nodes: incomingNodes,
                };
              }

              const existStartCursor = readField('id', existingNodes[0]);
              const existEndCursor = readField('id', existingNodes[existingNodes.length - 1]);

              const incomingStartCursorIndex = incomingNodes.findIndex((node: any) => {
                return readField('id', node) === existStartCursor;
              });

              const incomingEndCursorIndex = incomingNodes.findIndex((node: any) => {
                return readField('id', node) === existEndCursor;
              });

              if (incomingStartCursorIndex === -1 && incomingEndCursorIndex === -1) {
                const cursor = args?.input?.cursor;
                // cursor 위치를 incomingNodes에서 찾기
                const cursorIndex = incomingNodes.findIndex((node: any) => {
                  return readField('id', node) === cursor;
                });
                // 못 찾으면 보수적으로 전체를 앞에 붙이기
                if (cursorIndex === -1) {
                  const result = mergeById(incomingNodes, existingNodes, { readField, mergeObjects });
                  return {
                    ...existing,
                    ...incoming,
                    hasNext: existing.hasNext === false ? false : incoming.hasNext,
                    nodes: result,
                  };
                }
                // 분할: cursor 이전 → prev, cursor 이후 → next
                const prevNodes = incomingNodes.slice(0, cursorIndex + 1); // exclusive
                const nextNodes = incomingNodes.slice(cursorIndex + 1); // exclusive
                let resultNodes = existingNodes;
                if (prevNodes.length > 0) {
                  resultNodes = mergeById(prevNodes, resultNodes, { readField, mergeObjects });
                }
                if (nextNodes.length > 0) {
                  resultNodes = mergeById(resultNodes, nextNodes, { readField, mergeObjects });
                }
                return {
                  ...existing,
                  ...incoming,
                  hasNext: existing.hasNext === false ? false : incoming.hasNext,
                  nodes: resultNodes,
                };
              }

              const prevNodes = incomingNodes.slice(0, incomingStartCursorIndex + 1);
              const nextNodes = incomingNodes.slice(incomingEndCursorIndex + 1);

              let resultNodes = existingNodes;

              if (prevNodes.length > 0) {
                resultNodes = mergeById(prevNodes, resultNodes, { readField, mergeObjects });
              }

              if (nextNodes.length > 0) {
                resultNodes = mergeById(resultNodes, nextNodes, { readField, mergeObjects });
              }

              return {
                ...existing,
                ...incoming,
                hasNext: existing.hasNext === false ? false : incoming.hasNext,
                nodes: resultNodes,
              };
            },
          },

          memos: relayStylePagination(),
        },
      },
      Query: {
        fields: {
          channelImages: relayStylePagination(['channelId']),
          channelFiles: relayStylePagination(['channelId']),
          scheduledMessages: relayStylePagination(),
          scheduledMessagesByChannel: relayStylePagination(['channelId']),
          chatBotMessages: {
            keyArgs: ['input', ['userId', 'limit']],
          },
          searchEnglishText: {
            keyArgs: ['input', ['sentence']],

            merge(existing, incoming) {
              const existingElasticSearchResults = existing ? [...existing.elasticSearchResults] : [];

              return {
                offsetSkipCount: incoming.offsetSkipCount,
                elasticSearchResults: [...existingElasticSearchResults, ...incoming.elasticSearchResults],
              };
            },
          },
          channels: {
            keyArgs: ['userId', 'channelMemberType', 'input', ['search']],
            merge(existing, incoming, { args, readField }) {
              if (!existing) {
                return incoming;
              }
              if (!incoming) {
                return existing;
              }

              const existingNodes = (existing.nodes || []).slice(0);
              const incomingNodes = incoming.nodes || [];

              if (args) {
                const offset = args?.input?.offset || 0;
                for (let i = 0; i < incomingNodes.length; ++i) {
                  existingNodes[offset + i] = incomingNodes[i];
                }
              } else {
                existingNodes.push(...incomingNodes);
              }

              // ID 기반 중복 제거 (polling으로 인한 채널 순서 변경 시 중복 방지)
              const seen = new Set<string>();
              const deduped = existingNodes.filter((node: any) => {
                if (!node) return false;
                const id = readField('id', node) as string;
                if (!id || seen.has(id)) return false;
                seen.add(id);
                return true;
              });

              return {
                ...existing,
                ...incoming,
                nodes: deduped,
              };
            },
          },
          archivedChannels: offsetLimitPagination([
            'userId',
            'channelMemberType',
            ['input', ['offset', 'limit']],
          ]),
          searchMathQuestions: {
            keyArgs: ['input', ['managementCode']],

            merge(existing, incoming) {
              const existingMathQuestionSearchResults = existing
                ? [...existing.mathQuestionSearchResults]
                : [];

              return {
                offsetSkipCount: incoming.offsetSkipCount,
                mathQuestionSearchResults: [
                  ...existingMathQuestionSearchResults,
                  ...incoming.mathQuestionSearchResults,
                ],
              };
            },
          },
          // chatBotMessages: {
          //   keyArgs: ['input', ['userid']],
          // },

          metaContentModules: tableOffsetLimitPagination(['metaChapterId']),
          metaContents: tableOffsetLimitPagination(['searchBy', 'filterBy', 'orderBy']),
          metaContentsNoOffset: offsetLimitPagination(['searchBy', 'filterBy', 'orderBy']),
          curriculums: {
            keyArgs: ['where', 'orderBy'],
            merge(_existing, incoming, { args }) {
              // 테이블 UI에서는 현재 페이지 데이터만 표시
              // fetchMore 시에는 새로운 데이터로 교체
              if (args?.after) {
                return incoming;
              }
              // 첫 로드 시에는 기존 데이터가 없으므로 incoming 반환
              return incoming;
            },
          },
          studyActivities: tableOffsetLimitPagination(['metaContentModuleId']),
          studyModulesOnDailyCurriculum: offsetLimitPagination(['dailyCurriculumId', 'input', ['orderBy']]),
          studyActivitiesByStudyActivityTypes: offsetLimitPagination(['searchBy', 'filterBy', 'orderBy']),
          // 과목·폴리아 상태·페이지별 목록과 totalCount가 다른 조회의 캐시를 덮지 않도록 분리한다.
          searchCurriculums: tableOffsetLimitPagination(['input']),
          blockActivitiesOnBlockMetaContentModule: {
            keyArgs: ['input', ['mcmId']],
            merge(existing, incoming, { args }) {
              const mergedEdges = existing ? ((existing && existing.edges) || []).slice(0) : [];

              if (args) {
                // Assume an offset of 0 if args.offset omitted.
                const offset = args?.input?.offset || 0;
                for (let i = 0; i < incoming.edges.length; ++i) {
                  mergedEdges[offset + i] = incoming.edges[i];
                }
              } else {
                mergedEdges.push.apply(mergedEdges, incoming.edges);
              }

              return {
                ...existing,
                ...incoming,
                edges: mergedEdges,
              };
            },
          },
          studyActivitiesOnMetaContentModule: offsetLimitPagination(['searchBy', 'filterBy', 'orderBy']),
          appSessions: tableOffsetLimitPagination(),
          users: tableOffsetLimitPagination(['input', ['search', 'limit', 'offset']]),
          products: offsetLimitPagination(),
          feedsFromManagerGroup: offsetLimitPagination(['input', ['groupId']]),
          feedsFromManagerGroupWithDueDate: offsetLimitPagination(['input', ['groupId', 'dueDate']]),
          studentGroupFeeds: tableOffsetLimitPagination([
            'input',
            [
              'groupId',
              'filterBy',
              ['dueDateEnd', 'dueDateStart', 'feedCompleteStatus', 'feedTypes', 'title'],
            ],
          ]),
          words: tableOffsetLimitPagination(['searchBy', 'filterBy', 'limit', 'offset']),
          metaContentStats: tableOffsetLimitPagination([
            'input',
            [
              'metaContentId',
              'limit',
              'offset',
              'filterBy',
              ['activityType', 'statsCategory', 'viewType'],
              'orderBy',
              ['direction', 'field'],
            ],
          ]),
          multipleChoiceSelectionStatsByMetaContent: tableOffsetLimitPagination([
            'input',
            [
              'metaContentId',
              'limit',
              'offset',
              'filterBy',
              ['activityType', 'grade'],
              'orderBy',
              ['direction', 'field'],
            ],
          ]),
          knowledgeTags: tableOffsetLimitPagination([
            'input',
            [
              'limit',
              'offset',
              'filterBy',
              [
                'label',
                'activitySetType',
                'activitySetPropertyValues',
                'studyUnitId',
                'subject',
                'activitySetAttributeIds',
              ],
              'orderBy',
              ['direction', 'field'],
            ],
          ]),
          knowledgeMaps: tableOffsetLimitPagination(['limit', 'offset', 'filterBy', ['title']]),
          progressFeedsByDateRange: {
            keyArgs: ['input', ['groupId']],
            merge(existing = [], incoming) {
              return [...existing, ...incoming];
            },
          },
          groupNotices: {
            keyArgs: ['input', ['studentId', 'groupId']],
            merge: mergeGroupPostsById,
          },
          groupMemos: {
            keyArgs: ['input', ['studentId', 'groupId']],
            merge: mergeGroupPostsById,
          },
          myGroupNotices: {
            keyArgs: ['input', ['date', 'groupId']],
          },
        },
      },
      Curriculum: {
        fields: {
          connectedStudyActivities: offsetLimitPagination(['searchBy', 'filterBy', 'orderBy']),
          linkedCurriculumsConnection: offsetLimitPagination(['input', ['keyWord']]),
        },
      },
      Post: {
        fields: {
          comments: offsetLimitPagination([]),
        },
      },
      Course: {
        fields: {
          classSessionManagementList: {
            merge: true,
          },
        },
      },
      StudyActivity: {
        fields: {
          normalReferenceInformations: tableOffsetLimitPagination(['filterBy', 'searchBy']),
          officialReferenceInformations: tableOffsetLimitPagination(['filterBy', 'searchBy']),
          metadata: {
            merge: mergeField(),
          },
        },
      },
      StudyActivityInstance: {
        fields: {
          normalReferenceInformations: tableOffsetLimitPagination(['filterBy', 'searchBy']),
          officialReferenceInformations: tableOffsetLimitPagination(['filterBy', 'searchBy']),
          metadata: {
            merge: mergeField(),
          },
          totalStudyStat: {
            merge: mergeField(),
          },
          deviceStudyStats: {
            merge: mergeArrayByIndex(),
          },
        },
      },
      LearningActivity: {
        fields: {
          activityEstimations: {
            merge: mergeArrayByField('nodeId'),
          },
          activityEstimationResults: {
            merge: mergeArrayByField('nodeId'),
          },
        },
      },
      StudyActivityInstanceQuestion: {
        fields: {
          subQuestions: { merge: mergeArrayByIndex() },
        },
      },

      StudyActivityInstanceMultipleChoiceSubQuestion: {
        fields: {
          question: { read: trimSoftHyphen },
          choiceItemList: { merge: mergeArrayByIndex() },
          relatedStudyActivityInstances: { merge: mergeArrayByIndex() },
        },
      },
      StudyActivityInstanceShortAnswerSubQuestion: {
        fields: {
          relatedStudyActivityInstances: { merge: mergeArrayByIndex() },
        },
      },
      StudyActivityInstanceMathQuestion: {
        fields: {
          subQuestions: { merge: mergeArrayByIndex() },
        },
      },
      StudyActivityInstanceWordMemory: {
        fields: {
          items: { merge: mergeArrayByIndex() },
        },
      },
      StudyActivityInstanceSentenceStudy: {
        fields: {
          slashList: { merge: mergeArrayByIndex() },
          grammarList: { merge: mergeArrayByIndex() },
          syntaxList: { merge: mergeArrayByIndex() },
          memoList: { merge: mergeArrayByIndex() },
          history: { merge: mergeArrayByIndex() },
        },
      },
      StudyActivityInstanceSentenceStudyHistory: {
        fields: {
          slashList: { merge: mergeArrayByIndex() },
          syntaxList: { merge: mergeArrayByIndex() },
        },
      },
      SentenceStudyTranslationGradingError: {
        fields: {
          // 채점 구독은 myFeedback을 싣지 않아 라이브로 생성된 error 엔티티엔 이 칸이 비어 있다.
          // 미작성 = "피드백 준 적 없음" = null 이 도메인 진실이므로 빈칸을 null로 읽어
          // useFragment 완결성(complete)을 만족시킨다. 실제 값은 쿼리/뮤테이션/탭전파가 채운다.
          myFeedback: {
            read(existing = null) {
              return existing;
            },
          },
        },
      },
      StudyActivityInstanceSentenceMatchBlankBlocks: {
        fields: {
          result: {
            merge: true,
          },
        },
      },
      StudyActivityInstanceSentenceMatchWholeBlocks: {
        fields: {
          result: {
            merge: true,
          },
        },
      },
      StudyActivityInstanceSentenceCompleteBlocks: {
        fields: {
          result: {
            merge: true,
          },
        },
      },
      StudyActivityInstanceSentenceCompleteWholeBlocks: {
        fields: {
          result: {
            merge: true,
          },
        },
      },
      ChannelMember: {
        fields: {
          lastReadMessage: {
            merge(existing, incoming, { readField }) {
              if (!existing) {
                return incoming;
              }
              if (!incoming) {
                return incoming;
              }

              const existingCreatedAt = readField('createdAt', existing);
              const incomingCreatedAt = readField('createdAt', incoming);

              if (!existingCreatedAt && incomingCreatedAt) {
                return incoming;
              }
              if (existingCreatedAt && !incomingCreatedAt) {
                return existing;
              }
              if (!existingCreatedAt && !incomingCreatedAt) {
                return incoming;
              }

              if (new Date(incomingCreatedAt! as string) > new Date(existingCreatedAt! as string)) {
                return incoming;
              }

              return existing;
            },
          },
        },
      },
      User: {
        fields: {
          AIDigitalTextbook: {
            merge: mergeField(),
          },
          // profileUrl CDN 변환은 read가 아니라 merge(쓰기 시점)에서 한다.
          // read로 하면 User 객체의 result-identity 캐싱이 깨져 me/user 쿼리가 무한 리페치된다.
          // merge는 쓰기 시 1회만 변환해 저장하므로 읽기 참조가 안정적이다. profileImageToCdn은 idempotent.
          // 미설정('' 또는 기본 이미지)→이니셜 fallback은 표시 지점(resolveProfileImageSrc)이 담당한다.
          profileUrl: {
            merge(_existing, incoming: string | undefined) {
              if (!incoming) return incoming;
              return isProject('mildang') ? profileImageToCdn(incoming) : incoming;
            },
          },
        },
      },
      UserRoleType: {
        fields: {
          operationPermissions: {
            merge: mergeArrayByIndex(),
          },
        },
      },
      StudyActivityInstanceSentenceGrammarStudy: {
        fields: {
          result: {
            merge: true,
          },
        },
      },
      Student: {
        fields: {
          calendarDayItems: {
            keyArgs: ['input', ['managerId']],
            merge(existing = [], incoming, { readField }) {
              const extractKey = (current: any) => readField('id', current) as string;
              // properties of each two objects with the same id are being correctly merged('incoming' has higher priority)
              const merged = merge(keyBy(existing, extractKey), keyBy(incoming, extractKey));

              return Object.values(merged);
            },
          },
        },
      },
      GroupReplicaCurriculum: {
        fields: {
          groupReplicaCurriculumItem: {
            merge: true,
          },
        },
      },
      // StudyActivityInstanceMetaDataUnionType: {
      //   merge: true,
      // },
    } as StrictTypedTypePolicies,
  };
}

function createCache() {
  const cache = new InMemoryCache(getConfig());

  return cache;
}

export default createCache;

export type ApolloCache = ReturnType<typeof createCache>;
